// Chat models a tenant has configured. An agent version selects one instead of
// carrying its own copy of the provider, model and API key.

import { ReturnBanner } from "@/components/return-banner";
import { useRouter } from "@/lib/navigation";
import { useReturnTo } from "@/lib/return-to";
import { ModelRegistry, type RegistryRow } from "@/components/models/model-registry";
import { createLlmModel, deleteLlmModel, listLlmModels, testLlmModel } from "@/lib/api/endpoints";
import { useLlmModels, useModelCatalog } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";

export default function LlmModelsPage() {
  const returnTo = useReturnTo();
  const router = useRouter();

  const { data, isLoading, isError, error, refetch } = useLlmModels();
  const catalog = useModelCatalog();

  return (
    <>
      {returnTo ? <ReturnBanner to={returnTo} noun="an LLM provider" /> : null}
      <ModelRegistry
        onCreated={returnTo ? () => router.push(returnTo) : undefined}
        title="LLM Providers"
        noun="provider"
        description="Providers your agents run on. Configure a provider, its model and API key once, then select it when creating an agent — no need to paste the key again."
        providers={["openai", "anthropic", "gemini"]}
        catalogFor={(provider) => catalog.data?.chat[provider] ?? []}
        usedByNoun="agent versions"
        // Counts agent versions *and* the governance analyzer, so it cannot be
        // labelled "agent versions" without being wrong whenever governance uses it.
        usedByCountNoun="references"
        rows={data as RegistryRow[] | undefined}
        isLoading={isLoading}
        isError={isError}
        error={error}
        refetch={() => void refetch()}
        queryKey={qk.llmModels}
        create={createLlmModel}
        remove={deleteLlmModel}
        test={testLlmModel}
      />
    </>
  );
}

// Referenced so the list query and this page can't drift apart on the endpoint.
void listLlmModels;
